import { typographyTokens } from '../../theme/typographyTokens';
import { Box, Button, Divider, Typography, Menu, MenuItem, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useStreamExportTasks } from '../../services/tasksSlice/useStreamExportTasks';
import SpinnerOverlay from '../Spinner/Spinner';

const SepTableHeader = ({
  searchFilter,
  resultNumber,
  onModalOpen,
  hideExportButton,
  loading,
  isEditColumnApplied = false,
  toggleDrawer,
  appliedFilters,
  hideFilterButton,
  hideEditColumn, 
  tabs,
}: {
  count: number;
  sortBy?: string;
  sortAsc?: boolean;
  status?: string;
  searchFilter?: string;
  resultNumber: number;
  assigneeId?: string;
  onModalOpen: () => void;
  hideExportButton?: boolean;
  loading?: boolean;
  isEditColumnApplied?: boolean;
  toggleDrawer?: (open: boolean) => void;
  appliedFilters?: any;
  hideFilterButton?: boolean;
  hideEditColumn?: boolean;
  tabs?: React.ReactNode;
}): JSX.Element => {
  const { start: startExport, isLoading, progress: exportProgress, error: exportError, reset: resetExport } = useStreamExportTasks();
  const [option, setOption] = useState(null);
  const [val, setVal] = useState(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  useEffect(() => {
    if (exportError) {
      // Surface failures and clear state so the user can retry
      console.error('Export failed:', exportError);
      resetExport();
    }
  }, [exportError, resetExport]);

  const handleDownload = (opt: any) => {
    startExport(opt);
    setVal(opt)
    setOption(null)
  };

  const handleClick = (e: any) => {
    setOption(e.currentTarget)
  }

  return (
    <>
      {isLoading && (
        <SpinnerOverlay
          backgroundColor={isDarkMode ? theme.palette.background.default : theme.palette.content.white}
          textColor={isDarkMode ? theme.palette.text.primary : theme.palette.content.default}
          loaderText={
            exportProgress
              ? `Step ${exportProgress.step} of ${exportProgress.totalSteps}: ${exportProgress.message}`
              : `Preparing ${val === 'tasks' ? 'tasks' : 'SEPs'} export...`
          } />
      )}
      <Box sx={{ backgroundColor: isDarkMode ? theme.palette.background.default : theme.palette.content.white }}>
        {/* <Divider sx={{ borderColor: theme.palette.prime.blue }} /> */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap="8px"
          py="10px"
          sx={{
            px: { xs: '12px', md: '16px' },
            flexDirection: { xs: 'column', sm: 'row' },
            border: '1px solid',
            borderBottom: loading || resultNumber === 0 ? '1px solid' : '0',
            borderColor: theme.palette.supportingAdditional.tealContainerStroke,
            borderRadius: '6px 6px 0 0',
          }}
        >
          <Box display="flex" alignItems="center" gap="16px" flexWrap="wrap">
            {tabs}
            <Typography color={isDarkMode ? theme.palette.text.primary : theme.palette.content.default} fontSize={typographyTokens.fontSizes.body} fontWeight={typographyTokens.fontWeights.semibold}>
              {loading ? (
                'Loading'
              ) : (
                new Intl.NumberFormat('en-US').format(resultNumber)
              )}{' '}
              Results
              {searchFilter && ` for '${searchFilter}'`}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" flexWrap="nowrap">
            {!hideFilterButton && (
              <Button
                size="small"
                sx={{
                  py: '4px',
                  px: '10px',
                  mx: '8px',
                  textTransform: 'inherit',
                  border: '1px solid',
                  borderColor: appliedFilters?.filterApplied ? theme.palette.prime.blue : theme.palette.supportingAdditional.tealContainerStroke,
                  backgroundColor: appliedFilters?.filterApplied ? theme.palette.prime.blue : theme.palette.content.white,
                  color: appliedFilters?.filterApplied ? theme.palette.content.white : theme.palette.content.default,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ":hover": {
                    backgroundColor: theme.palette.prime.blue,
                    color: theme.palette.content.white,
                  }
                }}
                onClick={() => toggleDrawer?.(true)}
              >
                {appliedFilters?.filterApplied ? <i className="fa-solid fa-filter" style={{ padding: '3px 4px 0px 0px' }}></i> : <i className="fa-regular fa-filter" style={{ padding: '3px 4px 0px 0px' }}></i>}Filter SEPs
              </Button>
            )}
            {!hideEditColumn && (
            <Button
              variant="text"
              sx={{
                py: '4px',
                mx: '8px',
                textTransform: 'inherit',
                border: '1px solid',
                borderColor: isEditColumnApplied ? theme.palette.prime.blue : theme.palette.supportingAdditional.tealContainerStroke,
                backgroundColor: isEditColumnApplied ? theme.palette.prime.blue : theme.palette.content.white,
                color: isEditColumnApplied ? theme.palette.content.white : theme.palette.content.default,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ":hover": {
                  backgroundColor: theme.palette.prime.blue,
                  color: theme.palette.content.white,
                }
              }}
              onClick={() => onModalOpen()}
            >
              <i className="fa-solid fa-table-columns"></i>
              <Typography
                component="span"
                fontSize={typographyTokens.fontSizes.caption}
                fontWeight={typographyTokens.fontWeights.semibold}
                whiteSpace="nowrap"
                ml="6px"
              >
                Edit Columns
              </Typography>
            </Button>
            )}
            {!hideExportButton && (
              <>
                <Button
                  variant="text"
                  sx={{
                    py: '4px',
                    mx: '8px',
                    textTransform: 'inherit',
                    border: '1px solid',
                    borderColor: theme.palette.supportingAdditional.tealContainerStroke,
                    backgroundColor: theme.palette.content.white,
                    color: theme.palette.content.default,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ":hover": {
                      backgroundColor: theme.palette.prime.blue,
                      color: theme.palette.content.white,
                    }
                  }}
                  onClick={handleClick}
                >
                  <i
                    className="fa-regular fa-file"
                  ></i>
                  <Typography
                    component="span"
                    fontSize={typographyTokens.fontSizes.caption}
                    fontWeight={typographyTokens.fontWeights.semibold}
                    whiteSpace="nowrap"
                    ml="6px"
                  >
                    Export to CSV
                  </Typography>
                </Button>
                <Menu
                  anchorEl={option}
                  open={Boolean(option)}
                  onClose={() => setOption(null)}
                  PaperProps={{ sx: { backgroundColor: isDarkMode ? theme.palette.background.default : theme.palette.content.white } }}
                >
                  <MenuItem sx={{ color: isDarkMode ? theme.palette.text.primary : theme.palette.content.default }} onClick={() => handleDownload("seps")}>
                    Export SEP Level Data
                  </MenuItem>
                  <MenuItem sx={{ color: isDarkMode ? theme.palette.text.primary : theme.palette.content.default }} onClick={() => handleDownload("tasks")}>
                    Export Task Level Data
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SepTableHeader;
